<!-- components/staff/editStaff.vue -->
<template>
  <el-dialog :title="$t('staff.modifyStaff')" :visible.sync="visible" width="50%" @close="handleClose">
    <el-form ref="form" :model="editStaffInfo" label-width="120px">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item :label="$t('staff.name')" prop="username">
            <el-input v-model="editStaffInfo.username" :placeholder="$t('staff.requiredName')" />
          </el-form-item>
          <el-form-item :label="$t('email')" prop="email">
            <el-input v-model="editStaffInfo.email" :placeholder="$t('staff.optionalEmail')" />
          </el-form-item>
          <el-form-item :label="$t('staff.phone')" prop="tel">
            <el-input v-model="editStaffInfo.tel" :placeholder="$t('staff.requiredPhone')" />
          </el-form-item>
          <el-form-item :label="$t('staff.gender')" prop="sex">
            <el-select v-model="editStaffInfo.sex" :placeholder="$t('staff.requiredGender')" style="width: 100%">
              <el-option value="0" :label="$t('staff.male')" />
              <el-option value="1" :label="$t('staff.female')" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('staff.relCd')" prop="relCd">
            <el-select v-model="editStaffInfo.relCd" :placeholder="$t('staff.relCdPlaceholder')" style="width: 100%">
              <el-option v-for="(item,index) in editStaffInfo.relCds" :key="index" :label="item.name"
                :value="item.statusCd" />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('staff.address')" prop="address">
            <el-input v-model="editStaffInfo.address" :placeholder="$t('staff.requiredAddress')" />
          </el-form-item>
        </el-col>
        <el-col :span="12" style="text-align: center">
          <div class="photo-card">
            <div class="photo-preview" v-if="editStaffInfo.photoUrl === '' && hasCamera">
              <video ref="video" autoplay playsinline class="photo-media"></video>
              <canvas ref="canvas" style="display: none"></canvas>
            </div>
            <div class="photo-preview" v-else>
              <el-image v-if="editStaffInfo.photoUrl" :src="editStaffInfo.photoUrl" fit="cover" class="photo-media" />
              <el-image v-else src="/img/noPhoto.jpg" fit="cover" class="photo-media" />
            </div>
            <div class="photo-actions">
              <el-button type="primary" icon="el-icon-camera" size="small" @click="takePhoto" v-if="hasCamera">
                {{ $t("addOwnerMember.takePhoto") }}
              </el-button>
              <el-button size="small" type="primary" icon="el-icon-upload2" @click="_uploadPhoto">
                {{ $t('staff.uploadPhoto') }}
              </el-button>
            </div>
            <input type="file" ref="fileInput" accept=".jpg,.jpeg,.png" @change="_choosePhoto" style="display: none" />
          </div>
        </el-col>
      </el-row>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('staff.cancel') }}</el-button>
      <el-button type="primary" @click="editStaffSubmit">{{ $t('staff.edit') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { modifyStaff } from '@/api/staff/staffApi'
import { getDict, getCommunityId } from '../../api/community/communityApi'
import { uploadFile } from '@/api/staff/addStaffApi'


export default {
  name: 'EditStaff',
  props: {
    staffInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      visible: false,
      loading: false,
      editStaffInfo: {
        userId: '',
        username: '',
        email: '',
        tel: '',
        sex: '',
        address: '',
        photo: '',
        photoUrl: '',
        relCd: '',
        relCds: [],
        branchOrgs: [],
        departmentOrgs: [],
        parentOrgId: '',
        parentOrgName: '',
        parentTwoOrgId: '',
        orgNewName: '',
        orgId: ''
      },
      stream: null, // 摄像头视频流
      hasCamera: false, // 是否支持摄像头
    }
  },
  watch: {
    staffInfo: {
      immediate: true,
      handler(val) {
        if (val) {
          this.editStaffInfo = {
            ...val,
            username: val.name,
            photoUrl: this.getPhotoUrl(val)
          }
        }
      }
    }
  },
  methods: {
    normalizePhotoUrl(url) {
      if (!url || url === '/img/noPhoto.jpg') return ''
      return url
    },
    isDirectImageUrl(url) {
      return typeof url === 'string' && (url.startsWith('http') || url.startsWith('https') || url.startsWith('data:') || url.startsWith('/callComponent/'))
    },
    loadImageFromBlob(blob) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(url);
          resolve(img);
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        };
        img.src = url;
      });
    },
    canvasToJpegBlob(canvas, quality) {
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/jpeg", quality);
      });
    },
    scaleDownCanvas(sourceCanvas, ratio) {
      const targetCanvas = document.createElement("canvas");
      targetCanvas.width = Math.max(1, Math.round(sourceCanvas.width * ratio));
      targetCanvas.height = Math.max(1, Math.round(sourceCanvas.height * ratio));
      const ctx = targetCanvas.getContext("2d");
      ctx.drawImage(sourceCanvas, 0, 0, targetCanvas.width, targetCanvas.height);
      return targetCanvas;
    },
    async compressJpegFile(file, { maxBytes = 100 * 1024, maxDimension = 1080 } = {}) {
      if (!file || !(file instanceof Blob)) return file;
      if (file.type === "image/jpeg" && file.size <= maxBytes) return file;

      const img = await this.loadImageFromBlob(file);

      const canvas = document.createElement("canvas");
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      const scale = Math.min(maxDimension / iw, maxDimension / ih, 1);
      canvas.width = Math.max(1, Math.round(iw * scale));
      canvas.height = Math.max(1, Math.round(ih * scale));

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      let quality = 0.9;
      let blob = await this.canvasToJpegBlob(canvas, quality);

      while (blob && blob.size > maxBytes && quality > 0.15) {
        quality = Math.max(0.15, quality - 0.1);
        blob = await this.canvasToJpegBlob(canvas, quality);
      }

      let workingCanvas = canvas;
      while (blob && blob.size > maxBytes && (workingCanvas.width > 320 || workingCanvas.height > 320)) {
        workingCanvas = this.scaleDownCanvas(workingCanvas, 0.9);
        quality = Math.min(quality, 0.8);
        blob = await this.canvasToJpegBlob(workingCanvas, quality);
        while (blob && blob.size > maxBytes && quality > 0.15) {
          quality = Math.max(0.15, quality - 0.1);
          blob = await this.canvasToJpegBlob(workingCanvas, quality);
        }
      }

      const finalBlob = blob && blob.size ? blob : file;
      const name = typeof file.name === "string" && file.name ? file.name.replace(/\.[^/.]+$/, "") + ".jpg" : "photo.jpg";
      return new File([finalBlob], name, { type: "image/jpeg" });
    },
    // 初始化摄像头
    async startCamera() {
      try {
        // 基础兼容性判断
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.$message && this.$message.error("当前浏览器不支持摄像头调用");
          return;
        }

        // 先检测当前设备是否存在摄像头
        if (!navigator.mediaDevices.enumerateDevices) {
          // 某些非常老的浏览器没有 enumerateDevices，只能直接尝试 getUserMedia
          this.$message && this.$message.warning("无法检测摄像头设备，直接尝试打开摄像头");
        } else {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const hasCamera = devices.some((d) => d.kind === "videoinput");

          if (!hasCamera) {
            // this.$message && this.$message.error("未检测到摄像头设备");
            this.hasCamera = false;
            return;
          }
        }

        // 注意：video 在 template 里被 v-if 控制，必须等 DOM 渲染出来再取 ref。
        // 第一次可能因为权限弹窗很慢“刚好没问题”，第二次 getUserMedia 很快就会踩到这里。
        this.hasCamera = true;
        await this.$nextTick();

        // 请求摄像头权限（可根据需求设置前后摄像头）
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" }, // 前置可用 'user'
          audio: false,
        });

        const video = this.$refs.video;
        if (!video) {
          // 理论上不应发生：兜底避免抛 TypeError 被当成“权限/设备问题”。
          console.warn("video ref 未就绪，无法挂载摄像头流");
          return;
        }

        if ("srcObject" in video) {
          video.srcObject = this.stream;
        } else {
          // 旧浏览器兼容
          video.src = window.URL.createObjectURL(this.stream);
        }
        video.play();
      } catch (err) {
        console.error("打开摄像头失败", err);

        // 根据错误类型给更友好的提示
        if (err.name === "NotFoundError" || err.name === "OverconstrainedError") {
          this.$message && this.$message.error("未检测到可用的摄像头");
        } else if (err.name === "NotAllowedError" || err.name === "SecurityError") {
          this.$message && this.$message.error("摄像头权限被拒绝，请在浏览器中开启权限");
        } else {
          this.$message && this.$message.error("打开摄像头失败，请检查设备和权限");
        }
      }
    },
    stopCamera() {
      // 1. 停止所有 track
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      // 2. 清理 video
      const video = this.$refs.video;
      if (video) {
        if ("srcObject" in video) {
          video.srcObject = null;
        } else {
          video.src = "";
        }
        video.pause && video.pause();
      }
    },
    // 拍照 / 重新拍照
    takePhoto() {
      // 若当前已经有照片：点击“拍照”= 重新拍照（清空旧图 -> 展示预览 -> 重启摄像头）
      if (this.editStaffInfo.photoUrl) {
        if (!this.hasCamera) return;

        this.editStaffInfo.photo = "";
        this.editStaffInfo.photoUrl = "";

        // 允许重复选择同一文件（上传照片后也可再选同一张）
        if (this.$refs.fileInput) this.$refs.fileInput.value = "";

        // 先停再启，避免部分浏览器占用摄像头导致 getUserMedia 失败
        this.stopCamera();
        this.$nextTick(() => this.startCamera());
        return;
      }

      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      if (!video || !canvas) return;

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) {
        console.warn("无法获取视频分辨率");
        return;
      }

      const maxSize = 1080; // 限制最大宽高

      // 计算等比例缩放比例，保证宽高都不超过 1080
      const scale = Math.min(maxSize / vw, maxSize / vh, 1);

      const targetW = Math.round(vw * scale);
      const targetH = Math.round(vh * scale);

      // 设置 canvas 的内部绘制尺寸（真实图片尺寸）
      canvas.width = targetW;
      canvas.height = targetH;

      const ctx = canvas.getContext("2d");
      // 把当前视频帧画到指定大小
      ctx.drawImage(video, 0, 0, targetW, targetH);

      // 转成 Blob
      canvas.toBlob(
        (blob) => {
          if (!blob) return;
          // 如果 _doUploadImage 需要 File：
          const file = new File([blob], "photo.jpg", { type: "image/jpeg" });
          this._doUploadImage(file);
          this.stopCamera(); // 拍照后停止摄像头
        },
        "image/jpeg",
        0.8
      );
    },
    getPhotoUrl(row) {
      const photoUrl = this.normalizePhotoUrl(row.photoUrl)
      if (photoUrl) return photoUrl
      const photo = this.normalizePhotoUrl(row.photo)
      if (photo) {
        if (this.isDirectImageUrl(photo)) return photo
        return `/callComponent/download/getFile/file?fileId=${row.photo}&communityId=${getCommunityId()}&time=${new Date().getTime()}`
      }
      const fileId = this.normalizePhotoUrl(row.fileId)
      if (fileId) {
        if (this.isDirectImageUrl(fileId)) return fileId
        return `/callComponent/download/getFile/file?fileId=${row.fileId}&communityId=${getCommunityId()}&time=${new Date().getTime()}`
      }
      if (row.urls && row.urls.length > 0) {
        return row.urls[0]
      }
      return ''
    },
    open(row) {
      this.editStaffInfo = {
        ...row,
        username: row.name,
        photo: row.photo || row.fileId || '',
        photoUrl: this.getPhotoUrl(row)
      }
      this.getRelCd()
      this.visible = true
      this.$nextTick(async () => {
        await this.startCamera();
      });
    },
    async getRelCd() {
      const data = await getDict('u_org_staff_rel', "rel_cd")
      this.editStaffInfo.relCds = data
      this.$forceUpdate()
    },
    handleClose() {
      this.visible = false
      this.stopCamera()
    },
    validateForm() {
      if (!this.editStaffInfo.username) {
        this.$message.error(this.$t('staff.requiredName'))
        return false
      }
      if (!this.editStaffInfo.tel) {
        this.$message.error(this.$t('staff.requiredPhone'))
        return false
      }
      if (this.editStaffInfo.sex === '') {
        this.$message.error(this.$t('staff.requiredGender'))
        return false
      }
      if (!this.editStaffInfo.address) {
        this.$message.error(this.$t('staff.requiredAddress'))
        return false
      }
      return true
    },
    async editStaffSubmit() {
      if (!this.validateForm()) return

      try {
        this.loading = true
        const data = {
          ...this.editStaffInfo,
          name: this.editStaffInfo.username,
          staffId: this.editStaffInfo.userId
        }

        await modifyStaff(data)
        this.visible = false
        this.$message.success(this.$t('common.operationSuccess'))
        this.$emit('success')
        this.handleClose()
      } catch (error) {
        this.$message.error(error.message)
      } finally {
        this.loading = false
      }
    },
    _uploadPhoto() {
      this.$refs.fileInput.click()
    },
    _choosePhoto(event) {
      const files = event.target.files
      if (files && files.length > 0) {
        const file = files[0]
        if (file.size > 1024 * 1024 * 2) {
          this.$message.error(this.$t('addOwnerMember.validate.photoSize'))
          event.target.value = ''
          return
        }
        this._doUploadImage(file)
        event.target.value = ''
      }
    },
    async _doUploadImage(file) {
      try {
        const compressedFile = await this.compressJpegFile(file, { maxBytes: 100 * 1024, maxDimension: 1080 })
        const formData = new FormData()
        formData.append('uploadFile', compressedFile)
        formData.append('communityId', getCommunityId())

        const res = await uploadFile(formData)
        this.editStaffInfo.photo = res.fileId
        this.editStaffInfo.photoUrl = res.url
      } catch (error) {
        this.$message.error(this.$t('addOwnerMember.uploadFailed'))
      }
    },
  }
}
</script>

<style scoped>
.photo-card {
  width: 100%;
  max-width: 360px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.photo-preview {
  width: 100%;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.photo-media {
  width: 150px;
  height: 150px;
  object-fit: cover;
  display: block;
}

video.photo-media {
  width: 320px;
  height: 160px;
  max-width: 100%;
}

.photo-actions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

.photo-actions .el-button {
  width: 120px;
  margin-left: 0;
  margin-right: 0;
}
</style>
